import { useEffect, useState } from "react";
const KEY = "panda1818.bewild.auth";
export function getToken(): string {
  return sessionStorage.getItem(KEY) || localStorage.getItem(KEY) || "";
}
export function setToken(token: string, remember = false) {
  sessionStorage.removeItem(KEY);
  localStorage.removeItem(KEY);
  if (token) (remember ? localStorage : sessionStorage).setItem(KEY, token);
  window.dispatchEvent(new Event("panda-auth"));
}
// 游客下单（2026-09-26）：查询邮箱 + 查询密码只留在当前标签页会话里；
// 它能看到卡密，所以不进 localStorage（与 dujiao 自带前端的做法一致）。
const GUEST_KEY = "panda1818.bewild.guest";
export interface GuestAuth {
  email: string;
  password: string;
}
export function getGuestAuth(): GuestAuth | null {
  try {
    const raw = JSON.parse(sessionStorage.getItem(GUEST_KEY) || "null");
    return raw &&
      typeof raw.email === "string" &&
      typeof raw.password === "string" &&
      raw.email &&
      raw.password
      ? { email: raw.email, password: raw.password }
      : null;
  } catch {
    return null;
  }
}
export function setGuestAuth(auth: GuestAuth | null) {
  try {
    if (auth)
      sessionStorage.setItem(
        GUEST_KEY,
        JSON.stringify({ email: auth.email.trim(), password: auth.password.trim() }),
      );
    else sessionStorage.removeItem(GUEST_KEY);
  } catch {}
}
// dujiao 的游客凭证头：Guest base64url(邮箱 + 换行 + 查询密码)，RawURLEncoding，不带 = 填充。
export function guestAuthorization({ email, password }: GuestAuth): string {
  const bytes = new TextEncoder().encode(email.trim() + "\n" + password.trim());
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return (
    "Guest " +
    btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
  );
}
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}
export async function api<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  if (!path.startsWith("/") || path.startsWith("//"))
    throw new Error("无效请求地址");
  const token = getToken(),
    guest = token ? null : getGuestAuth();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers.Authorization = "Bearer " + token;
  else if (guest) headers.Authorization = guestAuthorization(guest);
  if (body !== undefined) headers["Content-Type"] = "application/json";
  let response: Response;
  try {
    response = await fetch("/api" + path, {
      method: body === undefined ? "GET" : "POST",
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: signal
        ? AbortSignal.any([signal, AbortSignal.timeout(25000)])
        : AbortSignal.timeout(25000),
    });
  } catch (e) {
    if (signal?.aborted) throw e;
    throw new ApiError("连接暂时中断，请检查网络后重试。", 0);
  }
  let payload;
  try {
    payload = await response.json();
  } catch {
    throw new ApiError("服务暂时不可用，请稍后重试。", 0);
  }
  if (!response.ok || payload?.code !== 1) {
    if (
      response.status === 401 &&
      token === getToken() &&
      token &&
      !path.startsWith("/auth/")
    )
      setToken("");
    throw new ApiError(
      payload?.message || payload?.msg || "请求未完成",
      response.status,
      payload || {},
    );
  }
  return payload.data as T;
}
export function useApi<T>(path: string | null, version: unknown = 0) {
  const [record, setRecord] = useState<{
      data: T | null;
      path: string | null;
      version: unknown;
    }>({ data: null, path: null, version: null }),
    [loading, setLoading] = useState(!!path),
    [error, setError] = useState(""),
    [tick, setTick] = useState(0);
  useEffect(() => {
    if (!path) {
      setRecord({ data: null, path, version });
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setRecord({ data: null, path, version });
    setLoading(true);
    setError("");
    api<T>(path, undefined, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setRecord({ data, path, version });
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [path, tick, version]);
  return {
    data:
      record.path === path && record.version === version ? record.data : null,
    loading:
      !!path && (loading || record.path !== path || record.version !== version),
    error,
    reload: () => setTick((n) => n + 1),
  };
}
export function navigate(path: string) {
  if (!path.startsWith("/") || path.startsWith("//")) return;
  history.pushState({}, "", path);
  dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, behavior: "instant" });
}
export const signInFor = (path: string) =>
  navigate("/login?next=" + encodeURIComponent(path));
export function safeNext(raw: string | null): string {
  return raw?.startsWith("/") &&
    !raw.startsWith("//") &&
    !raw.includes("\\") &&
    !/[\u0000-\u001f]/.test(raw) &&
    !raw.startsWith("/login")
    ? raw
    : "/subscribe";
}
export function paymentTarget(raw: string): string {
  try {
    const u = new URL(raw, location.origin);
    if (["http:", "https:"].includes(u.protocol)) return u.href;
  } catch {}
  throw new Error("支付地址无效，请在我的订单中重试或联系客服。");
}
